/**
 * Always-visible audio controls: three volume sliders (Master / Ambient /
 * SFX) plus a mute-all toggle. Sits in the top-right column below the
 * minimap. Changes apply live via GameAudio.setMix and persist to the server
 * (same SoundStore the editor uses).
 */

import type { GameAudio, VolumeMix } from './Audio';
import { getSoundConfig, saveSoundConfig, type SoundConfig } from './SoundStore';

const IS_MOBILE = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

export function buildSoundControls(audio: GameAudio): void {
  const current: SoundConfig = { ...getSoundConfig() };
  let muted = false;
  let saveTimer: ReturnType<typeof setTimeout> | null = null;

  const apply = (): void => {
    const live: VolumeMix = muted
      ? { master: 0, ambient: current.ambient, sfx: current.sfx }
      : { master: current.master, ambient: current.ambient, sfx: current.sfx };
    audio.setMix(live);
  };

  const queueSave = (): void => {
    if (saveTimer !== null) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      saveTimer = null;
      void saveSoundConfig(current).catch((err) => console.warn('saveSoundConfig failed', err));
    }, 300);
  };

  // Stack in the right-side column UNDER the WeatherHUD (which lives at
  // top:300 and is ~110 px tall). `IS_MOBILE` kept around in case we want to
  // tighten the layout further on small screens.
  void IS_MOBILE;
  const topOffset = 420;

  const panel = document.createElement('div');
  panel.style.cssText = [
    'position:fixed',
    `top:${topOffset}px`, 'right:8px',
    `width:${IS_MOBILE ? 140 : 220}px`,
    'background:rgba(15,20,25,0.88)',
    'color:#eee',
    'border:1px solid rgba(255,255,255,0.15)',
    'border-radius:8px',
    'padding:8px 10px',
    'font:12px/1.3 system-ui,sans-serif',
    'box-shadow:0 2px 8px rgba(0,0,0,0.5)',
    'z-index:6',
    'user-select:none',
    '-webkit-user-select:none',
  ].join(';');

  const title = document.createElement('div');
  title.textContent = '🔊 Sound';
  title.style.cssText = 'font-weight:600;margin-bottom:6px;opacity:0.9';
  panel.appendChild(title);

  const sliderRow = (key: 'master' | 'ambient' | 'sfx', label: string): HTMLInputElement => {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;gap:6px;margin:3px 0';
    const lab = document.createElement('div');
    lab.textContent = label;
    lab.style.cssText = `width:${IS_MOBILE ? 44 : 56}px;opacity:0.85;font-size:11px`;
    const sl = document.createElement('input');
    sl.type = 'range';
    sl.min = '0';
    sl.max = '1';
    sl.step = '0.01';
    sl.value = String(current[key]);
    sl.style.cssText = 'flex:1;accent-color:#6ab4ff;min-width:0';
    sl.addEventListener('input', () => {
      current[key] = Number(sl.value);
      if (muted) {
        muted = false;
        refreshMute();
      }
      apply();
      queueSave();
    });
    row.appendChild(lab);
    row.appendChild(sl);
    panel.appendChild(row);
    return sl;
  };

  sliderRow('master', 'Master');
  sliderRow('ambient', 'Ambient');
  sliderRow('sfx', 'SFX');

  const muteBtn = document.createElement('button');
  muteBtn.type = 'button';
  muteBtn.style.cssText = [
    'margin-top:6px', 'width:100%',
    'background:rgba(40,60,80,0.6)',
    'color:#eee',
    'border:1px solid rgba(255,255,255,0.15)',
    'border-radius:6px',
    'padding:5px 8px',
    'font:11px/1 system-ui,sans-serif',
    'cursor:pointer',
  ].join(';');
  const refreshMute = (): void => {
    muteBtn.textContent = muted ? '🔊 Unmute' : '🔇 Mute all';
    muteBtn.style.background = muted ? 'rgba(160,70,60,0.7)' : 'rgba(40,60,80,0.6)';
  };
  muteBtn.addEventListener('click', () => {
    muted = !muted;
    refreshMute();
    apply();
  });
  refreshMute();
  panel.appendChild(muteBtn);

  document.body.appendChild(panel);
}
